'use client';

import { CSSProperties, FC } from 'react';
import { ImageField } from '@prismicio/client';
import classNames from 'classnames';
import { getFigurePaddingTopWithMaxHeight } from '@/utils';
import { IconSvg, Image, PrismicImageWithAlt } from '@/components';
import styles from './banner.module.scss';

export type BannerLocationItem = {
  active?: boolean;
  location_slug?: string | null;
  image?: ImageField;
};

export const BANNER_IMAGE_SIZES = `(min-width: 2048px)  2008px, (min-width: 1536px)  1496px,
        (min-width: 1280px)  1240px, (min-width: 1024px) 984px,
        (min-width: 768px) 728px, 100vw`;

export const BANNER_MOBILE_SIZES = '(min-width: 640px) 610px, 100wv';

const AMBIENT_PLACEHOLDER = `${
  process.env.NEXT_PUBLIC_REVERSE_PROXY_LINK ?? ''
}/images/brands/ambient-placeholder-treez.png`;

type BannerPrimaryLayout = {
  max_height?: number | null;
  max_height_mobile?: number | null;
  border_radius?: number | null;
};

type BannerSectionStyleOptions = {
  fullWidth?: boolean;
  titleMaxWidth?: number | null;
  titleMaxWidthMobile?: number | null;
  descriptionMaxWidth?: number | null;
  descriptionMaxWidthMobile?: number | null;
};

export const getBannerSectionStyle = (
  primary: BannerPrimaryLayout,
  options: BannerSectionStyleOptions = {},
): CSSProperties =>
  ({
    ...(primary.max_height_mobile && {
      '--banner-max-height-mobile': `${primary.max_height_mobile}px`,
    }),
    ...(primary.max_height && {
      '--banner-max-height-desktop': `${primary.max_height}px`,
    }),
    ...(options.fullWidth === false && {
      '--banner-border-radius': `${primary.border_radius ?? 0}px`,
    }),
    ...(options.fullWidth === undefined &&
      primary.border_radius != null && {
        '--banner-border-radius': `${primary.border_radius}px`,
      }),
    ...(options.titleMaxWidth && {
      '--banner-title-max-width': `${options.titleMaxWidth}%`,
    }),
    ...(options.titleMaxWidthMobile && {
      '--banner-title-max-width-mobile': `${options.titleMaxWidthMobile}%`,
    }),
    ...(options.descriptionMaxWidth && {
      '--banner-description-max-width': `${options.descriptionMaxWidth}%`,
    }),
    ...(options.descriptionMaxWidthMobile && {
      '--banner-description-max-width-mobile': `${options.descriptionMaxWidthMobile}%`,
    }),
  } as CSSProperties);

export const BannerMissingPlaceholder: FC = () => (
  <div>
    <IconSvg name="image" />
    <p>
      <b>There is no active banner</b>
      <br />
      Kindly establish an active banner
    </p>
  </div>
);

type BannerFigureProps = {
  imageField?: ImageField | null;
  imageSizes: string;
  classCss: string;
  adjustAspectRatio?: boolean;
  maxHeight?: number | null;
  bannerActive: boolean;
  showMissingBannerPlaceholder: boolean;
  backgroundColor?: string;
  primaryBackgroundColor?: string | null;
};

const BannerFigure: FC<BannerFigureProps> = ({
  imageField,
  imageSizes,
  classCss,
  adjustAspectRatio,
  maxHeight,
  bannerActive,
  showMissingBannerPlaceholder,
  backgroundColor,
  primaryBackgroundColor,
}) => {
  const paddingTop = getFigurePaddingTopWithMaxHeight(
    imageField,
    adjustAspectRatio,
    maxHeight,
  );

  return (
    <figure
      className={classNames(classCss, {
        [styles.placeholder]: showMissingBannerPlaceholder,
        [styles.page_banner__figure_aspect_ratio]: Boolean(paddingTop),
      })}
      style={{
        backgroundColor,
        ...(paddingTop && { paddingTop }),
      }}
    >
      {bannerActive && imageField?.url ? (
        <PrismicImageWithAlt
          defaultAlt="Banner"
          fetchPriority="high"
          field={imageField}
          fill
          sizes={imageSizes}
          style={{
            objectFit: 'cover',
            objectPosition: 'center',
          }}
        />
      ) : (
        bannerActive &&
        !primaryBackgroundColor && (
          <Image
            alt="Strain page banner"
            fill
            src={AMBIENT_PLACEHOLDER}
            style={{ objectFit: 'cover', objectPosition: 'center' }}
          />
        )
      )}
      {showMissingBannerPlaceholder && <BannerMissingPlaceholder />}
    </figure>
  );
};

type BannerImagesProps = {
  banner?: BannerLocationItem;
  bannerActive: boolean;
  showMissingBannerPlaceholder: boolean;
  primaryBackgroundColor?: string | null;
  adjustAspectRatioDesktop?: boolean;
  adjustAspectRatioMobile?: boolean;
  maxHeightDesktop?: number | null;
  maxHeightMobile?: number | null;
};

export const BannerImages: FC<BannerImagesProps> = ({
  banner,
  bannerActive,
  showMissingBannerPlaceholder,
  primaryBackgroundColor,
  adjustAspectRatioDesktop,
  adjustAspectRatioMobile,
  maxHeightDesktop,
  maxHeightMobile,
}) => {
  const backgroundColor =
    bannerActive && primaryBackgroundColor
      ? (primaryBackgroundColor as string)
      : undefined;

  return (
    <>
      <BannerFigure
        adjustAspectRatio={adjustAspectRatioDesktop}
        backgroundColor={backgroundColor}
        bannerActive={bannerActive}
        classCss={styles.page_banner__desktop}
        imageField={banner?.image}
        imageSizes={BANNER_IMAGE_SIZES}
        maxHeight={maxHeightDesktop}
        primaryBackgroundColor={primaryBackgroundColor}
        showMissingBannerPlaceholder={showMissingBannerPlaceholder}
      />
      <BannerFigure
        adjustAspectRatio={adjustAspectRatioMobile}
        backgroundColor={backgroundColor}
        bannerActive={bannerActive}
        classCss={styles.page_banner__mobile}
        imageField={
          (banner?.image as ImageField & { mobile?: ImageField })?.mobile
        }
        imageSizes={BANNER_MOBILE_SIZES}
        maxHeight={maxHeightMobile}
        primaryBackgroundColor={primaryBackgroundColor}
        showMissingBannerPlaceholder={showMissingBannerPlaceholder}
      />
    </>
  );
};
